import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Info } from "lucide-react";
import { getCommunications } from "@/lib/api";
import { COMM_LABEL, ONB_EVENT_LABEL, fmtWhen } from "@/lib/comm";

const FILTERS = [
  { key: "", label: "All communications" },
  { key: "registration_received", label: "Registration Received" },
  { key: "interview_invitation", label: "Interview Invitation" },
  { key: "digital_onboarding", label: "Digital Onboarding" },
];

function ResultPill({ result }) {
  const ok = result === "sent";
  return (
    <span className={`pill ${ok ? "approved" : "crit"}`} data-testid={`comm-result-${result}`}>
      {ok ? "Sent" : "Failed"}
    </span>
  );
}

export default function Communications() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");

  useEffect(() => {
    setRows(null);
    getCommunications(type ? { type } : undefined).then(setRows).catch(() => setRows([]));
  }, [type]);

  return (
    <div data-testid="communications-view">
      <div className="crumb">
        <Link to="/dashboard">Staffing Home</Link> › <strong>Communications</strong>
      </div>

      <div className="panel" style={{ marginTop: 12 }}>
        <div className="ph">
          <h2><Mail size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />Email activity log</h2>
          <span className="sp" />
          <select
            className="ca-input"
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="comm-filter"
            style={{ maxWidth: 240 }}
          >
            {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
        <div className="pb flush">
          {rows === null ? (
            <div className="empty" data-testid="comm-loading">Loading communications…</div>
          ) : rows.length === 0 ? (
            <div className="empty" data-testid="comm-empty">No email communications recorded yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Type</th>
                  <th>Recipient</th>
                  <th>Result</th>
                  <th>Sent</th>
                  <th>Related</th>
                  <th>Send</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`comm-row-${c.id}`}>
                    <td data-label="Candidate">
                      <Link className="cand-name" to={`/candidates/${c.registrationId}`} data-testid={`comm-candidate-${c.id}`}>
                        {c.candidateName || c.registrationId}
                      </Link>
                      <div className="mut">{c.registrationId}</div>
                    </td>
                    <td data-label="Type">{COMM_LABEL[c.type] || c.type}</td>
                    <td data-label="Recipient" className="mut">{c.recipientEmail}</td>
                    <td data-label="Result"><ResultPill result={c.result} />{c.error && <div className="mut" style={{ fontSize: ".72rem", marginTop: 2 }}>{c.error}</div>}</td>
                    <td data-label="Sent" className="mut">{fmtWhen(c.sentAt || c.createdAt)}</td>
                    <td data-label="Related" className="mut">
                      {c.sessionCode ? `Session ${c.sessionCode}` : c.onboardingEvent ? (ONB_EVENT_LABEL[c.onboardingEvent] || c.onboardingEvent) : "—"}
                    </td>
                    <td data-label="Send">
                      <span className={`pill ${c.kind === "resend" ? "awaiting" : ""}`} data-testid={`comm-kind-${c.id}`}>
                        {c.kind === "resend" ? "Resend" : "Original"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="note-compact" data-testid="comm-scope-guard">
        <Info size={15} />
        <span>
          <strong>Actual send events only.</strong> Every row is a persisted email event recorded at the
          moment it was sent — never inferred from candidate status. Refreshes and repeated reads never
          create new sends.
        </span>
      </div>
    </div>
  );
}
